import React, { useRef, type ReactNode } from 'react';
import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ANALYTICS_BRIDGE_CHANNEL } from '@atlassian/jira-portfolio-plan-wizard';
import { getEvent, MountEvent, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';

import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import { getPlanId, getWizardVersion, stripPlanWizardParams } from './utils';

type Props = {
	children: ReactNode;
};

const useAnalyticsClient = (): {
	sendEvent: (arg1: UIAnalyticsEvent) => void;
} => {
	const analyticsWebClientRef = useRef(getAnalyticsWebClientPromise());
	return {
		sendEvent: (analyticsEvent: UIAnalyticsEvent) => {
			analyticsWebClientRef.current.then((webClient) =>
				webClient.sendEvent(getEvent(analyticsEvent)),
			);
		},
	};
};

export function AnalyticsProvider({ children }: Props) {
	const { sendEvent } = useAnalyticsClient();
	return (
		<AnalyticsListener channel={ANALYTICS_BRIDGE_CHANNEL} onEvent={sendEvent}>
			{children}
		</AnalyticsListener>
	);
}

/**
 * Fires the `plan created` event whenever the user are redirected from the legacy creation plan wizard
 */
export function FirePlanCreationEventFromThePrevWizard() {
	const planId = useRef(getPlanId(window.location)).current;
	const version = useRef(getWizardVersion(window.location)).current;
	const nextUrl = useRef(stripPlanWizardParams(window.location)).current;

	// When finishing the plan creation on the legacy plan wizard, the redirected URL will have &wizardversion=2
	if (version !== 2) {
		return null;
	}

	const fireEvent = (analyticsEvent: UIAnalyticsEvent) => {
		fireTrackAnalytics(analyticsEvent, 'plan created', {
			planId,
			version,
		});

		// Remove the param to prevent the user from triggering the event more than once by reloading or bookmarking
		window.history.replaceState({}, '', nextUrl);
	};
	return <MountEvent onMount={fireEvent} />;
}
